import api from "@services/api";
import { DrivingRoute, RoadPath } from "@shared/interfaces/routing.interface";

/** A stretch of the travel path drawn along the roads, in the shape the map reads from the Google Roads API */
export interface SnappedStretch {
  coords: google.maps.LatLngLiteral[];
  /** Which recorded point each road point stands for (the road points added between them have none) */
  originalIndexes: (number | undefined)[];
}

/**
 * Driving distance and time through the platform's own route service (OSRM, through the Backend)
 * @param origin Start point
 * @param destination End point
 * @returns The route, or null when the service has no answer, is not set up or cannot be reached (the caller falls back to Google)
 */
export const readPlatformDrivingRoute = async (
  origin: google.maps.LatLngLiteral,
  destination: google.maps.LatLngLiteral
): Promise<DrivingRoute | null> => {
  try {
    const { data } = await api.get("/routing/route", {
      params: {
        originLatitude: origin.lat,
        originLongitude: origin.lng,
        destinationLatitude: destination.lat,
        destinationLongitude: destination.lng
      }
    });

    return data?.status === "success" && data.result ? data.result as DrivingRoute : null;
  } catch (error) {
    return null;
  }
};

/**
 * Draw up to 100 recorded points along the roads through the platform's own route service (OSRM, through the Backend)
 * @param path Recorded points
 * @returns The stretch, or null when the service is not set up or cannot be reached (the caller falls back to Google)
 */
export const matchPathOnPlatform = async (path: google.maps.LatLngLiteral[]): Promise<SnappedStretch | null> => {
  try {
    const { data } = await api.post("/routing/match", { path });
    const result: RoadPath | null = data?.status === "success" ? data.result : null;

    if (!result) return null;

    return {
      coords: result.points,
      originalIndexes: result.originalIndexes.map((index) => (index === null ? undefined : index))
    };
  } catch (error) {
    return null;
  }
};
